import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDateTime } from "@/lib/cc";
import { LayoutDashboard, Users, CalendarClock, BarChart3, TrendingUp, CheckCircle2, RefreshCw } from "lucide-react";

const STAGE_COLORS = {
  applied: "var(--cc-text-soft)",
  screening: "#9ca3af",
  interview: "#0091c5",
  offer: "#f59e0b",
  hired: "#10b981",
  rejected: "#ef4444",
};

export default function InterviewDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/interviews");
      setData(data);
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  if (loading || !data) return <div className="cc-card p-10 text-center text-[var(--cc-text-soft)]">Loading dashboard…</div>;

  const maxDist = Math.max(1, ...Object.values(data.score_distribution || {}));
  const funnelMax = Math.max(1, data.progression.applied);
  const upcomingFiltered = data.upcoming_interviews.filter((r) => typeFilter === "all" || r.type === typeFilter);

  return (
    <div className="fade-up space-y-6" data-testid="interview-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2">Reporting</div>
          <h1 className="font-display text-4xl font-bold flex items-center gap-3">
            <LayoutDashboard className="w-8 h-8" /> Interview Dashboard
          </h1>
          <p className="text-[var(--cc-text-soft)] mt-1">Upcoming interviews, scores and the candidate-progression funnel.</p>
        </div>
        <button className="btn-ghost" onClick={fetchData} data-testid="dashboard-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: CalendarClock, label: "Upcoming (next 30d)", value: data.upcoming_count, sub: `${data.pending_confirmation} pending · ${data.confirmed} confirmed`, t: "kpi-upcoming" },
          { icon: CheckCircle2, label: "Completed (last 8w)", value: data.completed_count, sub: `avg score ${data.average_score?.toFixed(2)} / 5`, t: "kpi-completed" },
          { icon: TrendingUp, label: "Hired (last 8w)", value: data.progression.hired, sub: `${data.conversion_rate}% conversion`, t: "kpi-hired" },
          { icon: Users, label: "Active pipeline", value: data.progression.applied, sub: `${data.pipeline_breakdown.rejected} rejected`, t: "kpi-active" },
        ].map((k) => (
          <div key={k.label} className="cc-card p-5" data-testid={k.t}>
            <div className="flex items-center justify-between">
              <k.icon className="w-5 h-5 text-[var(--cc-primary)]" />
              <span className="label-eyebrow">{k.label}</span>
            </div>
            <div className="font-display text-4xl font-bold mt-2">{k.value}</div>
            <div className="text-xs text-[var(--cc-text-soft)] mt-1">{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Score distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="cc-card p-5" data-testid="score-distribution-card">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-4 h-4 text-[var(--cc-primary)]" />
            <h3 className="font-display font-bold text-lg">Score distribution (last 8 weeks)</h3>
          </div>
          <div className="flex items-end gap-4 h-44 mt-6">
            {Object.entries(data.score_distribution).map(([range, count]) => (
              <div key={range} className="flex-1 flex flex-col items-center">
                <div className="text-xs mb-1 font-semibold">{count}</div>
                <div className="w-full rounded-t-md transition-all"
                     style={{ height: `${(count / maxDist) * 100}%`, background: "var(--cc-primary)", minHeight: "4px" }}
                     data-testid={`bar-${range}`} />
                <div className="text-xs mt-2 text-[var(--cc-text-soft)]">{range}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Progression funnel */}
        <div className="cc-card p-5" data-testid="funnel-card">
          <h3 className="font-display font-bold text-lg mb-4">Candidate progression</h3>
          <div className="space-y-2">
            {["applied", "screening", "interview", "offer", "hired"].map((stage) => {
              const v = data.progression[stage];
              const pct = (v / funnelMax) * 100;
              return (
                <div key={stage} className="flex items-center gap-3" data-testid={`funnel-${stage}`}>
                  <div className="w-20 text-xs uppercase tracking-wide text-[var(--cc-text-soft)]">{stage}</div>
                  <div className="flex-1 bg-[var(--cc-surface)] rounded-md h-7 relative overflow-hidden">
                    <div className="h-full transition-all" style={{ width: `${Math.max(pct, 6)}%`, background: STAGE_COLORS[stage] }} />
                    <div className="absolute inset-0 flex items-center px-3 text-xs font-semibold">{v} <span className="ml-2 opacity-60">{Math.round(pct)}%</span></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Upcoming */}
      <div className="cc-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--cc-border)] flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Upcoming interviews</h3>
          <select className="cc-select max-w-[180px] py-1.5" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="upcoming-type-filter">
            <option value="all">All types</option>
            <option value="phone_screen">Phone screen</option>
            <option value="in_person">In person</option>
            <option value="panel">Panel</option>
          </select>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Candidate</th>
              <th className="px-5 py-3 label-eyebrow">Job</th>
              <th className="px-5 py-3 label-eyebrow">Type</th>
              <th className="px-5 py-3 label-eyebrow">Date</th>
              <th className="px-5 py-3 label-eyebrow">Interviewer</th>
              <th className="px-5 py-3 label-eyebrow">Status</th>
            </tr>
          </thead>
          <tbody>
            {upcomingFiltered.map((r) => (
              <tr key={r.interview_id} className="border-t border-[var(--cc-border)]" data-testid={`upcoming-row-${r.interview_id}`}>
                <td className="px-5 py-3 font-medium">{r.candidate_name}</td>
                <td className="px-5 py-3">{r.job_title}</td>
                <td className="px-5 py-3 capitalize">{r.type?.replace("_", " ")}</td>
                <td className="px-5 py-3 text-xs">{fmtDateTime(r.scheduled_at)}</td>
                <td className="px-5 py-3 text-xs">{r.interviewer || "—"}</td>
                <td className="px-5 py-3"><span className={`pill ${r.status === "scheduled" ? "pill-info" : "pill-warning"}`}>{r.status}</span></td>
              </tr>
            ))}
            {upcomingFiltered.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]">No upcoming interviews.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Completed */}
      <div className="cc-card overflow-hidden">
        <div className="px-5 py-3 border-b border-[var(--cc-border)]">
          <h3 className="font-display font-bold text-lg">Completed (last 8 weeks)</h3>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--cc-surface)]">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow">Candidate</th>
              <th className="px-5 py-3 label-eyebrow">Job</th>
              <th className="px-5 py-3 label-eyebrow">Date</th>
              <th className="px-5 py-3 label-eyebrow">Type</th>
              <th className="px-5 py-3 label-eyebrow">Avg score</th>
              <th className="px-5 py-3 label-eyebrow">Scorecards</th>
            </tr>
          </thead>
          <tbody>
            {data.completed_interviews.map((r) => (
              <tr key={r.interview_id} className="border-t border-[var(--cc-border)]" data-testid={`completed-row-${r.interview_id}`}>
                <td className="px-5 py-3 font-medium">{r.candidate_name}</td>
                <td className="px-5 py-3">{r.job_title}</td>
                <td className="px-5 py-3 text-xs">{fmtDateTime(r.scheduled_at)}</td>
                <td className="px-5 py-3 capitalize">{r.type?.replace("_", " ")}</td>
                <td className="px-5 py-3 font-semibold">{r.average_score ? r.average_score.toFixed(2) : "—"}</td>
                <td className="px-5 py-3"><span className="pill pill-neutral">{r.scores_count}</span></td>
              </tr>
            ))}
            {data.completed_interviews.length === 0 && (
              <tr><td colSpan="6" className="text-center py-10 text-[var(--cc-text-soft)]">No completed interviews in the last 8 weeks.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
